import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../app/Store";
import { getSiteGroups, SiteGroup, siteGroupError } from "./SiteGroupsApi";
import { UserAccessType } from "../user/Models";

export const SiteGroupsContext = createContext<ReturnType<typeof useLoadSiteGroups> | undefined>(
  undefined
);

export function useSiteGroups() {
  const groups = useContext(SiteGroupsContext);
  if (!groups) {
    throw new Error("Site group consumers must be inside InitApp.");
  }
  return groups;
}

// InitApp owns one list and polling loop for the authenticated session.
export function useLoadSiteGroups(enabled: boolean) {
  const user = useSelector((state: RootState) => state.user);
  const flags = useSelector((state: RootState) => state.featureFlags?.sitesFeatureFlags);
  const ownerKey = JSON.stringify([user.oid, user.siteIds, user.userType, flags]);
  const [state, setState] = useState<{ ownerKey: string; sites: SiteGroup[]; error: string }>({
    ownerKey: "",
    sites: [],
    error: "",
  });
  const [refreshCount, setRefreshCount] = useState(0);
  const refresh = useCallback(() => setRefreshCount((value) => value + 1), []);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout>;
    const load = async () => {
      try {
        const sites = await getSiteGroups(controller.signal);
        if (!controller.signal.aborted) {
          setState({
            ownerKey,
            sites: sites.filter((group) => !group.isAdminOnly || user.userType === UserAccessType.Admin),
            error: "",
          });
        }
      } catch (error) {
        if (!controller.signal.aborted) {
          setState({ ownerKey, sites: [], error: siteGroupError(error) });
        }
      } finally {
        if (!controller.signal.aborted) {
          timer = setTimeout(() => void load(), 30_000);
        }
      }
    };
    void load();
    // Recheck access after each response so a slow request cannot overlap the next refresh.
    window.addEventListener("focus", refresh);
    return () => {
      controller.abort();
      clearTimeout(timer);
      window.removeEventListener("focus", refresh);
    };
  }, [enabled, ownerKey, refreshCount, refresh]);

  return {
    loading: enabled && state.ownerKey !== ownerKey,
    sites: enabled && state.ownerKey === ownerKey ? state.sites : [],
    error: enabled && state.ownerKey === ownerKey ? state.error : "",
    refresh,
  };
}
